import React from 'react';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { staggerContainer, staggerItem } from '../../animations/variants';

const UserList = ({ users, onUserClick, loading }) => {
  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
      }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  if (!users || users.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: 2,
        py: 6,
      }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: '#F0F4F8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PersonIcon sx={{ fontSize: 40, color: '#BDBDBD' }} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          No users found
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#999999',
            fontFamily: 'Metropolis, sans-serif',
            textAlign: 'center',
            maxWidth: 300,
            fontWeight: 500,
          }}
        >
          Create your first user to get started
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
      <List sx={{ p: 0 }}>
        {users.map((user, index) => (
          <motion.div key={user.id || user.username} variants={staggerItem}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => onUserClick(user.username)}
                sx={{
                  py: 2,
                  px: 3,
                  '&:hover': {
                    backgroundColor: '#F9FBFD',
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      backgroundColor: user.enabled ? '#4698D3' : '#BDBDBD',
                      width: 40,
                      height: 40,
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: 'Metropolis, sans-serif',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          color: user.enabled === false ? '#999999' : '#253E66',
                        }}
                      >
                        {user.username}
                      </Typography>
                      {user.enabled === false && (
                        <Chip
                          label="Disabled"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontFamily: 'Metropolis, sans-serif',
                            fontWeight: 600,
                            backgroundColor: 'rgba(231, 76, 60, 0.1)',
                            border: '1px solid rgba(231, 76, 60, 0.2)',
                            color: '#E74C3C',
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    user.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <EmailIcon sx={{
                          fontSize: '0.875rem',
                          color: '#999999',
                        }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'Metropolis, sans-serif',
                            color: '#666666',
                            fontSize: '0.8rem',
                          }}
                        >
                          {user.email}
                        </Typography>
                      </Box>
                    )
                  }
                />

                <ChevronRightIcon sx={{ color: '#BDBDBD' }} />
              </ListItemButton>
            </ListItem>

            {index < users.length - 1 && (
              <Divider sx={{ borderColor: '#E5E5E5' }} />
            )}
          </motion.div>
        ))}
      </List>
      </motion.div>
    </Box>
  );
};

export default UserList;
